import React, { useState } from "react";
import NavBar from "../components/NavBar";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import LeadDetails from "../components/LeadDetails";
import Loading from "../components/Loading";
import SmartFilterPanel from "../components/SmartFilterPanel";
import LeadsTable from "../components/LeadsTable";
import { useSmartFilter } from "../hooks/useSmartFilter";
import api from "../api";

/**
 * Main LeadPage component with integrated smart filter system
 * Uses the aligned smart filter system for advanced lead management
 */
function LeadPage() {
  // Use smart filter hook - this replaces all manual filter state management
  const {
    leads,
    leadsVersion,
    loading,
    error,
    pagination,
    filterStats,
    hasActiveFilters,
    getActiveFilterCount,
    clearAllFilters,
    goToPage,
    changePageSize,
    updateLeadInList, // Add this function from useSmartFilter hook
  } = useSmartFilter();

  // Modal state for lead details
  const [selectedLead, setSelectedLead] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

  // Handle row click to open lead details
  const handleRowClick = async (leadId) => {
    try {
      const response = await api.getLead(leadId);
      if (response && response.data) {
        setSelectedLead(response.data);
        setIsModalOpen(true);
        toast.success("Lead details loaded");
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Handle lead update from LeadDetails component
  const handleLeadUpdate = (updatedLead) => {
    // Optimistic UI update - immediately update the lead in the table
    if (updateLeadInList) {
      updateLeadInList(updatedLead);
    }

    // Update the selected lead for the modal
    setSelectedLead(updatedLead);

    // Show success message (the actual API call is handled in LeadDetails component)
    toast.success("Lead updated successfully");
  };

  if (loading && leads.length === 0) {
    return <Loading currentPage={"/lead"} />;
  }

  return (
    <div>
      <NavBar currentPage="/lead" />
      <div className="p-6">
        {/* Header with title, filter toggle, and stats */}
        <div className="flex justify-between items-center mb-4 max-w-6xl mx-auto">
          <div className="flex items-center space-x-4">
            <h1 className="text-2xl font-bold">Lead Management</h1>
            {hasActiveFilters() && (
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm">
                {getActiveFilterCount()} filters active
              </span>
            )}
            {loading && (
              <span className="text-gray-500 text-sm">Filtering...</span>
            )}
          </div>

          <div className="flex items-center space-x-3">
            {/* Filter Panel Toggle */}
            <button
              onClick={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
              className={`px-4 py-2 rounded-md font-medium ${
                isFilterPanelOpen
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              {isFilterPanelOpen ? "Hide Filters" : "Show Filters"}
            </button>

            {/* Clear Filters */}
            {hasActiveFilters() && (
              <button
                onClick={clearAllFilters}
                className="px-4 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200"
              >
                Clear All
              </button>
            )}
          </div>
        </div>

        {/* Filter Stats */}
        {filterStats && Object.keys(filterStats).length > 0 && (
          <div className="bg-blue-50 rounded-lg p-4 mb-4 max-w-6xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <div className="font-semibold text-blue-900">Total Results</div>
                <div className="text-blue-700">
                  {filterStats.totalMatched || pagination.total || 0}
                </div>
              </div>
              <div>
                <div className="font-semibold text-blue-900">Contacted</div>
                <div className="text-blue-700">
                  {filterStats.contactedCount || "N/A"}
                </div>
              </div>
              <div>
                <div className="font-semibold text-blue-900">Avg Savings</div>
                <div className="text-blue-700">
                  ${(filterStats.avgTotalSavings || 0).toLocaleString()}
                </div>
              </div>
              <div>
                <div className="font-semibold text-blue-900">
                  Total Potential
                </div>
                <div className="text-blue-700">
                  ${(filterStats.totalPotentialRevenue || 0).toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Smart Filter Panel */}
        {isFilterPanelOpen && (
          <div className="mb-6 max-w-6xl mx-auto">
            <SmartFilterPanel />
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4 max-w-6xl mx-auto">
            {error}
          </div>
        )}

        {/* Leads Table */}
        <LeadsTable
          key={`leads-table-${leadsVersion}-${leads?.length || 0}`}
          leads={leads}
          leadsVersion={leadsVersion}
          handleRowClick={handleRowClick}
          loading={loading}
          hasActiveFilters={hasActiveFilters}
        />

        {/* Enhanced Pagination */}
        <div className="max-w-6xl mx-auto mt-4 flex justify-between items-center">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <span>
              Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
              {Math.min(pagination.page * pagination.limit, pagination.total)}{" "}
              of {pagination.total} results
            </span>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={() => goToPage(pagination.page - 1)}
              disabled={pagination.page <= 1 || loading}
              className="inline-flex h-9 items-center justify-center rounded-md bg-black px-3 py-2 text-sm font-medium text-white shadow transition-colors hover:bg-gray-900 disabled:opacity-50 disabled:pointer-events-none"
            >
              Previous
            </button>

            <span className="text-gray-700 text-sm">
              Page {pagination.page} of {pagination.totalPages}
            </span>

            <button
              onClick={() => goToPage(pagination.page + 1)}
              disabled={pagination.page >= pagination.totalPages || loading}
              className="inline-flex h-9 items-center justify-center rounded-md bg-black px-3 py-2 text-sm font-medium text-white shadow transition-colors hover:bg-gray-900 disabled:opacity-50 disabled:pointer-events-none"
            >
              Next
            </button>

            <select
              value={pagination.limit}
              onChange={(e) => changePageSize(parseInt(e.target.value, 10))}
              disabled={loading}
              className="border border-gray-300 rounded-md p-2 focus:outline-none focus:border-gray-900 disabled:opacity-50"
            >
              <option value={5}>5 per page</option>
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
          </div>
        </div>
      </div>

      {/* Lead Details Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg max-w-4xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Lead Details</h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              >
                &times;
              </button>
            </div>
            {selectedLead && (
              <LeadDetails
                selectedLead={selectedLead}
                onLeadUpdate={handleLeadUpdate}
              />
            )}
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setIsModalOpen(false)}
                className="rounded-md bg-black px-4 py-2 text-white hover:bg-gray-900"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LeadPage;
